import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { getApiUrl } from '../../../utils/apiConfig';

const TipoMaterialDropdown = ({ selectedObra, setSelectedObra, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const norm = (s) => String(s || '').trim().toLowerCase();

  // Convertir string separado por | a array
  const getSelectedValues = () => {
    if (!selectedObra?.TipoMaterial) return [];
    return selectedObra.TipoMaterial.split('|').map(v => v.trim()).filter(Boolean);
  };

  // Convertir array a string separado por |
  const setSelectedValues = (valuesArray) => {
    const tipoMaterialString = valuesArray.filter(Boolean).join('|');
    setSelectedObra({ ...selectedObra, TipoMaterial: tipoMaterialString });
  };

  const selectedValues = getSelectedValues();

  // Cargar todos los tipos de material
  useEffect(() => {
    const fetchTipos = async () => {
      try {
        setLoading(true);
        const res = await axios.get(getApiUrl('/api/tipoMaterial'));
        const tipos = (res.data || [])
          .map(t => String(t.TipoMaterial || '').trim())
          .filter(Boolean);
        const unique = Array.from(new Set(tipos));
        // Ordenar alfabéticamente
        const sorted = unique.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
        setOptions(sorted);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar tipos de material:', err);
        setOptions([]);
        setLoading(false);
      }
    };
    fetchTipos();
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setInputValue(''); // Limpiar input al cerrar
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Manejar cambio de input (solo para buscar/filtrar)
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsOpen(true);
  }, []);

  // Toggle selección de un tipo (agregar o quitar)
  const toggleSelection = useCallback((tipo) => {
    const newValues = selectedValues.includes(tipo)
      ? selectedValues.filter(v => v !== tipo) // Quitar
      : [...selectedValues, tipo]; // Agregar
    setSelectedValues(newValues);
    // Mantener el dropdown abierto y limpiar el input para facilitar más búsquedas
    setInputValue('');
  }, [selectedValues]);

  // Remover un valor seleccionado (desde las pills)
  const removeValue = useCallback((tipo) => {
    const newValues = selectedValues.filter(v => v !== tipo);
    setSelectedValues(newValues);
  }, [selectedValues]);

  // Crear y seleccionar nuevo tipo
  const createAndSelect = useCallback(async (nombre) => {
    const clean = nombre.trim();
    if (clean.length < 2 || creating) return;
    
    try {
      setCreating(true);
      const response = await axios.post(
        getApiUrl('/api/tipoMaterial'), 
        { tipoMaterial: clean },
        { withCredentials: true }
      );
      
      // console.log('Tipo de material creado:', response.data);
      
      // Agregar a los seleccionados
      const newValues = [...selectedValues, clean];
      setSelectedValues(newValues);
      setInputValue('');
      setIsOpen(true); // Mantener abierto después de crear
      
      // Recargar opciones
      const res = await axios.get(getApiUrl('/api/tipoMaterial'));
      const tipos = (res.data || [])
        .map(t => String(t.TipoMaterial || '').trim())
        .filter(Boolean);
      const unique = Array.from(new Set(tipos));
      // Ordenar alfabéticamente
      const sorted = unique.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      setOptions(sorted);
      
    } catch (err) {
      console.error('Error al crear tipo de material:', err);
      if (err.response?.status === 409) {
        alert('Este tipo de material ya existe en la base de datos');
      } else {
        // Aunque falle, permitir usarlo localmente
        const newValues = [...selectedValues, clean];
        setSelectedValues(newValues);
        setInputValue('');
        alert('Error al guardar el tipo de material, pero se usará para esta obra');
      }
    } finally {
      setCreating(false);
    }
  }, [creating, selectedValues]);

  // Filtrar opciones según input
  const trimmed = inputValue.trim();
  const filteredOptions = trimmed.length >= 1
    ? options.filter(opt => norm(opt).includes(norm(trimmed)))
    : options;

  const hasExact = trimmed.length >= 2 && options.some(opt => norm(opt) === norm(trimmed));

  // Enter para crear si no hay match exacto
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    const q = inputValue.trim();
    if (q.length < 2) return;

    const exact = options.some(opt => norm(opt) === norm(q));
    if (!exact) {
      e.preventDefault();
      createAndSelect(q);
    } else {
      // Si existe, agregarlo a la selección
      const match = options.find(opt => norm(opt) === norm(q));
      if (match) {
        toggleSelection(match);
        setInputValue('');
      }
    }
  }, [inputValue, options, createAndSelect, toggleSelection]);

  // Mostrar valores seleccionados separados por " - " en el input
  const displayValue = selectedValues.length > 0 
    ? selectedValues.join(' - ')
    : '';

  const placeholder = 'Buscar o crear tipos de material…';

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
        Tipo de Material potencial a vender*
      </label>

      {/* Input simple mostrando valores separados por " - " */}
      <div className="relative">
        <input
          type="text"
          className="inputModal w-full border rounded bg-white shadow-sm px-3 py-2 text-sm"
          value={isOpen ? inputValue : displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            setInputValue('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          readOnly={!isOpen}
        />
      </div>

      {/* Dropdown de opciones - posicionado correctamente */}
      {isOpen && !disabled && (
        <ul 
          className="absolute w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-auto"
          style={{ 
            zIndex: 1100,
            maxHeight: '240px',
            marginTop: '4px'
          }}
        >
          {loading && (
            <li className="px-4 py-3 text-sm text-gray-400 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
              Cargando...
            </li>
          )}

          {/* Crear nuevo si no hay match exacto y hay texto suficiente */}
          {!loading && !hasExact && trimmed.length >= 2 && (
            <li
              onMouseDown={(e) => {
                e.preventDefault(); // Evitar que el input pierda el foco
                createAndSelect(trimmed);
              }}
              className="cursor-pointer px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-blue-50 border-b border-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span className="flex-1">
                {creating ? (
                  <span className="text-gray-500">Creando…</span>
                ) : (
                  <>
                    <span className="text-blue-600 font-medium">Crear nuevo:</span>{' '}
                    <span className="text-gray-700">"{trimmed}"</span>
                  </>
                )}
              </span>
            </li>
          )}

          {/* Resultados filtrados */}
          {!loading && filteredOptions.length > 0 && filteredOptions.map((tipo, idx) => {
            const isSelected = selectedValues.includes(tipo);
            return (
              <li
                key={idx}
                onMouseDown={(e) => {
                  e.preventDefault(); // Evitar que el input pierda el foco
                  toggleSelection(tipo);
                }}
                className={classNames(
                  'cursor-pointer px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors flex items-center gap-3',
                  { 'bg-blue-50': isSelected }
                )}
              >
                {/* Checkbox visual */}
                <div className={classNames(
                  'w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 transition-all',
                  isSelected 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'border-gray-300 hover:border-blue-400'
                )}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className={classNames(
                  'flex-1 truncate',
                  isSelected ? 'text-blue-700 font-medium' : 'text-gray-700'
                )}>
                  {tipo}
                </span>
              </li>
            );
          })}

          {/* Mensajes informativos */}
          {!loading && filteredOptions.length === 0 && trimmed.length < 2 && (
            <li className="px-4 py-3 text-sm text-blue-600 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Escribe al menos 2 caracteres para crear nuevo tipo
            </li>
          )}

          {!loading && filteredOptions.length === 0 && trimmed.length >= 2 && !hasExact && (
            <li className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              No se encontraron coincidencias. Presiona Enter para crear.
            </li>
          )}

          {!loading && options.length === 0 && trimmed.length === 0 && (
            <li className="px-4 py-3 text-sm text-gray-400 text-center">
              No hay tipos de material disponibles
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default TipoMaterialDropdown;